import ajax from './utils/ajax';
import { captcha } from './utils/recaptcha';
import { addToast } from './utils/toast';
import tplMultiFileListItem from './templates/multi_file_list_item.pug';

class MultiFileUploader {
  constructor(el, options) {
    const _defaults = {};
    this.opts = { ..._defaults, ...options };
    
    this.el = (typeof el === 'string') ? document.querySelector(el) : el;
    this.inputEl = this.el.querySelector('input[type="file"]');
    this.inputName = this.inputEl.getAttribute('name');
    this.maxFileCount = (this.inputEl.hasAttribute('data-max-file-count')) ? parseInt(this.inputEl.getAttribute('data-max-file-count')) : 3;
    const inputId = this.inputEl.getAttribute('id');
    const formId = this.inputEl.getAttribute('data-form-id');
    this.uploadBtnEl = this.el.querySelector(`label[for="${inputId}"]`);
    this.action = this.inputEl.getAttribute('data-action');
    this.deleteAction = this.inputEl.getAttribute('data-delete-action');
    this.formEl = document.querySelector(`#${formId}`);
    this.uploadListEl = this.el.querySelector('.multi-file-list');
    this.uploadedFiles = [];
    
    this.boundChange = e => this._onChange(e);
    this.boundDelete = e => this._onDelete(e);

    this.init();
  }

  init() {
    this.inputEl.addEventListener('change', this.boundChange, false);
  }

  destroy() {
    this.inputEl.removeEventListener('change', this.boundChange);
  }

  _onChange(e) {
    const files = Array.from(e.target.files) || [];
    if (files.length > 0) {
      const file = files[0];

      this.uploadBtnEl.classList.add('in-progress');

      // add upload list item:
      const listItemHtml = tplMultiFileListItem({
        classnames: ['multi-file-list-item', 'in-progress'],
        filenameOriginal: file.name,
        filenameNew: '',
        i18n: {
          complaint_file_upload_progress_title: Settings.contact.i18n.complaint_file_upload_progress_title,
          complaint_file_upload_btn_delete_label: Settings.contact.i18n.complaint_file_upload_btn_delete_label,
        }
      });
      this.uploadListEl.insertAdjacentHTML('beforeend', listItemHtml);
      const uploadListItemEl = this.uploadListEl.querySelector('.multi-file-list-item.in-progress');
      const btnDeleteEl = uploadListItemEl.querySelector('.btn');

      this.inputEl.disabled = true;

      captcha({ action: 'multi_file_upload' })
        .then(token => {

          const data = new FormData();
          const { upload_key, csrf_token } = this._collectFormData();
          // data.append('file', file);
          data.append(this.inputName, file);
          data.append('upload_key', upload_key);
          data.append('csrf_token', csrf_token);
          data.append('g-recaptcha-response', token);

          ajax({
            method: 'post',
            url: this.action,
            data,
            onUploadProgress: ({ loaded, total }) => {
              const percent = Math.round((loaded * 100) / total);
              uploadListItemEl.style.setProperty('--multi-file-list-item-progress', `${percent}%`);
            }
          })
            .then(response => {
              uploadListItemEl.classList.remove('in-progress');
    
              if (response.data.status === 'failure') {
                // server side error:
                this.inputEl.disabled = false;
                uploadListItemEl.parentNode.removeChild(uploadListItemEl);
                if (typeof response.data === 'string') {
                  addToast(response.data.message);
                } else if (typeof response.data.message === 'object' && typeof response.data.message.fields !== 'undefined') {
                  addToast(response.data.message.fields[this.inputName]);
                }
              } else {
                // success:
                uploadListItemEl.style.setProperty('--multi-file-list-item-progress', '0');
                uploadListItemEl.querySelector('span').textContent = file.name;
                btnDeleteEl.setAttribute('data-filename-new', response.data.message.file_name);
                btnDeleteEl.addEventListener('click', this.boundDelete, false);
    
                // collect file item:
                this.uploadedFiles.push({
                  el: uploadListItemEl,
                  filename: response.data.message.file_name,
                });

                if (this.uploadedFiles.length < this.maxFileCount) {
                  this.inputEl.disabled = false;
                }
              }
    
            })
            .catch(err => {
              console.error(err);
              uploadListItemEl.parentNode.removeChild(uploadListItemEl);
              this.inputEl.disabled = false;
              addToast(Settings.contact.i18n.complaint_file_upload_error);
            })
            .finally(() => {
              this.uploadBtnEl.classList.remove('in-progress');
              this.inputEl.value = '';
            });
        })
        .catch(err => {
          console.error(err);
          this.inputEl.disabled = false;
          addToast(Settings.contact.i18n.complaint_file_upload_error);
        });
      
    }
  }

  _onDelete(e) {
    e.preventDefault();
    const btnEl = e.target;
    const filename = btnEl.getAttribute('data-filename-new');

    btnEl.disabled = true;
    this.inputEl.disabled = true;

    captcha({ action: 'multi_file_delete' })
      .then(token => {
        const data = new FormData();
        const { upload_key, csrf_token } = this._collectFormData();
        data.append('file_name', filename);
        data.append('upload_key', upload_key);
        data.append('csrf_token', csrf_token);
        data.append('g-recaptcha-response', token);

        ajax({
          url: this.deleteAction,
          method: 'post',
          data,
        })
          .then(response => {
            // console.log(response.data);
            
            if (response.data.status === 'failure') {
              // server side error:
              addToast(response.data.message);
              this.inputEl.disabled = false;
            } else {
              // success:
              const fileIndex = this.uploadedFiles.findIndex(fileItem => fileItem.filename === filename);
              if (fileIndex > -1) {
                this.uploadListEl.removeChild(this.uploadedFiles[fileIndex].el);
                this.uploadedFiles.splice(fileIndex, 1);
              }
            }
          })
          .catch(err => {
            console.error(err);
            addToast(Settings.contact.i18n.complaint_file_delete_error);
          })
          .finally(() => {
            btnEl.disabled = false;
            if (this.uploadedFiles.length < this.maxFileCount) {
              this.inputEl.disabled = false;
            }
          });
        
      })
      .catch(err => {
        console.error(err);
        btnEl.disabled = false;
        this.inputEl.disabled = false;
        addToast(Settings.contact.i18n.complaint_file_delete_error);
      });
  }

  /**
   * Collect required form data for file upload and file delete
   * 
   * @returns {object}
   */
  _collectFormData() {
    const res = {
      upload_key: this.formEl.querySelector('input[name="upload_key"]').value,
      csrf_token: this.formEl.querySelector('input[name="csrf_token"]').value,
    };
    return res;
  }
}

export default MultiFileUploader;
